
import { GoogleGenAI, LiveServerMessage, Modality, FunctionDeclaration, Type } from '@google/genai';
import { createBlob } from './audioUtils';
import { LogFoodArgs, LogSymptomArgs, AppMode, Language, VoiceCommandArgs } from '../types';

interface LiveClientCallbacks {
  onStatusChange: (status: 'disconnected' | 'connecting' | 'connected' | 'error') => void;
  onFoodLogged: (food: LogFoodArgs) => void;
  onSymptomLogged: (symptom: LogSymptomArgs) => void;
  onCommandReceived: (args: VoiceCommandArgs) => void;
  onVolumeChange: (input: number, output: number) => void;
  onTranscriptionUpdate: (text: string) => void;
  onError: (error: string) => void;
}

const logFoodTool: FunctionDeclaration = {
  name: 'logFoodItem',
  parameters: {
    type: Type.OBJECT,
    description: 'Log a food item. STRICTLY RESTRICTED to edible ingredients, meals, or beverages. REJECT non-food items like vehicles, electronics, furniture, or feelings.',
    properties: {
      name: {
        type: Type.STRING,
        description: 'The name of the food item (e.g., "Apple", "Pomme"). Must be a culinary item.',
      },
      quantity: {
        type: Type.NUMBER,
        description: 'The numeric quantity eaten (e.g., 1, 100, 0.5).',
      },
      unit: {
        type: Type.STRING,
        description: 'The unit of measurement (e.g., "piece", "morceau", "grams", "cup").',
      },
    },
    required: ['name'],
  },
};

const logSymptomTool: FunctionDeclaration = {
  name: 'logSymptom',
  parameters: {
    type: Type.OBJECT,
    description: 'Log a physical side effect or symptom. Listen carefully for intensity levels or pain scores (0 to 10).',
    properties: {
      symptom: {
        type: Type.STRING,
        description: 'The physical sensation or symptom (e.g., "Headache", "Mal de tête", "Bloating"). Do not translate.',
      },
      severity: {
        type: Type.STRING,
        enum: ['mild', 'moderate', 'severe'],
        description: 'The intensity of the symptom. Map scores 1-3 to "mild", 4-6 to "moderate", 7-10 to "severe". Infer from tone if no score is provided.',
      },
    },
    required: ['symptom'],
  },
};

const voiceCommandTool: FunctionDeclaration = {
  name: 'processVoiceCommand',
  parameters: {
    type: Type.OBJECT,
    description: 'Execute a navigational or system command.',
    properties: {
      command: {
        type: Type.STRING,
        enum: ['start_meal', 'start_symptom'],
        description: 'The intent of the user. "start_meal" for record meal, "start_symptom" for record symptom/side effects.',
      },
      mealType: {
        type: Type.STRING,
        enum: ['breakfast', 'lunch', 'dinner', 'snack', 'afternoonSnack'],
        description: 'If user specified a meal category, map it here. Default to empty if unspecified.',
      }
    },
    required: ['command'],
  },
};

export class GeminiLiveClient {
  private ai: GoogleGenAI | null = null;
  private callbacks: LiveClientCallbacks;
  private inputAudioContext: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private scriptProcessor: ScriptProcessorNode | null = null;
  private session: any = null;
  private isConnected = false;
  private currentTranscription = '';
  
  // Turn deduplication to prevent echo artifacts
  private lastLoggedItemsThisTurn = new Set<string>();

  constructor(callbacks: LiveClientCallbacks) {
    this.callbacks = callbacks;
    const apiKey = process.env.API_KEY;
    
    if (apiKey) {
        try {
            this.ai = new GoogleGenAI({ apiKey: apiKey });
        } catch (e) {
            console.error("Failed to init Gemini Client", e);
        }
    } else {
        console.error("API Key missing in GeminiLiveService.");
    }
  }

  public async connect(mode: AppMode, language: Language, extraContext: string = "") {
    if (!this.ai) {
        this.callbacks.onError("API Configuration Missing (Check API Key)");
        return;
    }

    try {
      this.callbacks.onStatusChange('connecting');
      this.currentTranscription = '';
      this.lastLoggedItemsThisTurn.clear();

      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      this.inputAudioContext = new AudioContextClass();
      
      if (this.inputAudioContext.state === 'suspended') {
        await this.inputAudioContext.resume();
      }
      
      this.stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
            channelCount: 1,
            echoCancellation: true,
            autoGainControl: true,
            noiseSuppression: true
        } 
      });
      
      let systemInstruction = "";

      if (mode === 'command') {
          systemInstruction = language === 'fr'
              ? `Vous êtes le contrôleur vocal de VitalEat. Écoutez attentivement les commandes :
                 - 'enregistrer un repas', 'manger', 'noter déjeuner/dîner/snack' -> appeler processVoiceCommand avec 'start_meal'.
                 - Mapper 'petit-déjeuner' à 'breakfast', 'déjeuner' à 'lunch', 'dîner' à 'dinner', 'snack' ou 'goûter' à 'afternoonSnack'.
                 - 'enregistre un symptôme', 'noter mes effets secondaires', 'j'ai mal', 'noter mes douleurs' -> appeler processVoiceCommand avec 'start_symptom'.
                 IMPORTANT: Restez en écoute continue. Ne proposez jamais d'arrêter la session vous-même. Restez silencieux et n'utilisez que les outils.`
              : `You are the VitalEat voice controller. Listen for intent:
                 - 'record a meal', 'I'm eating', 'log breakfast/lunch/dinner' -> call processVoiceCommand with 'start_meal'.
                 - Map the specific meal name to the corresponding mealType enum if provided.
                 - 'record side effects', 'log a symptom', 'record a symptom', 'I feel sick', 'I have pain' -> call processVoiceCommand with 'start_symptom'.
                 IMPORTANT: Remain in continuous listening mode. Do not attempt to stop or end the session yourself. Stay silent and use tools only.`;
      } else if (language === 'fr') {
         if (mode === 'meal') {
            systemInstruction = `Vous êtes une IA stricte de saisie nutritionnelle. Votre SEUL but est d'appeler \`logFoodItem\`.
            
            RÈGLE SÉMANTIQUE OBLIGATOIRE (SEMANTIC FOOD WORLD):
            1. Vous ne devez enregistrer QUE ce qui est comestible par un humain (Aliments, Boissons, Ingrédients).
            2. REJETER STRICTEMENT tous les objets non comestibles, même s'ils sont dits clairement.
               - Exemples à REJETER: "Voiture", "Choc", "Table", "Chaise", "Idée", "Bruit", "Arbre".
               - Exemples à ACCEPTER: "Pomme", "Pain", "Eau", "Poulet", "Confiture abricot".
            
            AUTRES RÈGLES :
            3. Ne loggez que les NOUVEAUX éléments mentionnés.
            4. Le paramètre \`name\` doit être le nom de l'aliment en Français.
            ${extraContext}
            
            Restez silencieux. N'appelez l'outil QUE si l'input appartient au monde sémantique de la nourriture.`;
         } else {
            systemInstruction = `Vous êtes un assistant de santé silencieux. Appelez \`logSymptom\` pour les effets secondaires mentionnés.
            1. Ne loggez que les NOUVEAUX symptômes.
            2. Écoutez attentivement l'intensité (0-10).
            Ne parlez jamais. Restez silencieux.`;
         }
      } else {
         if (mode === 'meal') {
            systemInstruction = `You are a strict Nutrition AI Logger. Your ONLY goal is to call \`logFoodItem\`.

            MANDATORY SEMANTIC RULE (SEMANTIC FOOD WORLD):
            1. You must ONLY log items that are edible by humans (Food, Drinks, Ingredients).
            2. STRICTLY REJECT non-edible objects, vehicles, or concepts.
               - REJECT: "Car", "Shock", "Table", "Chair", "Phone", "Tree", "Idea".
               - ACCEPT: "Apple", "Bread", "Water", "Chicken", "Apricot Jam".
            
            OTHER RULES:
            3. Only log NEWLY mentioned items.
            4. Capture English names.
            ${extraContext}
            
            Generate absolute silence. DO NOT SPEAK. Only call the tool if the input belongs to the semantic world of food.`;
         } else {
            systemInstruction = `You are a silent health tracker. Call \`logSymptom\` for physical side effects mentioned.
            1. Only log NEW symptoms mentioned.
            2. Capture intensity levels carefully.
            Generate absolute silence. DO NOT SPEAK.`;
         }
      }

      let tools: any[] = [];
      if (mode === 'command') {
          tools = [{ functionDeclarations: [voiceCommandTool] }];
      } else if (mode === 'meal') {
          tools = [{ functionDeclarations: [logFoodTool, voiceCommandTool] }];
      } else {
          tools = [{ functionDeclarations: [logSymptomTool, voiceCommandTool] }];
      }

      const sessionPromise = this.ai.live.connect({
        model: 'gemini-2.5-flash-native-audio-preview-09-2025',
        callbacks: {
          onopen: () => {
            this.isConnected = true;
            this.callbacks.onStatusChange('connected');
            this.setupAudioInput(sessionPromise);
          },
          onmessage: async (message: LiveServerMessage) => {
            await this.handleMessage(message, sessionPromise);
          },
          onclose: () => {
            this.disconnect();
          },
          onerror: (e) => {
            console.error('Gemini Live Error:', e);
            this.callbacks.onError('Connection error occurred.');
            this.disconnect();
          },
        },
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: {
            voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Kore' } },
          },
          systemInstruction: systemInstruction,
          tools: tools,
          inputAudioTranscription: {},
        },
      });
      
      this.session = sessionPromise;

    } catch (err: any) {
      console.error("Failed to connect:", err);
      this.callbacks.onError(err.message || "Failed to access microphone or connect.");
      this.callbacks.onStatusChange('error');
    }
  }

  public disconnect() {
    this.isConnected = false;
    this.callbacks.onStatusChange('disconnected');

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    if (this.scriptProcessor) {
      this.scriptProcessor.disconnect();
      this.scriptProcessor = null;
    }
    if (this.inputAudioContext) {
      this.inputAudioContext.close();
      this.inputAudioContext = null;
    }

    this.session = null;
    this.lastLoggedItemsThisTurn.clear();
  }

  private setupAudioInput(sessionPromise: Promise<any>) {
    if (!this.inputAudioContext || !this.stream) return;

    const source = this.inputAudioContext.createMediaStreamSource(this.stream);
    this.scriptProcessor = this.inputAudioContext.createScriptProcessor(2048, 1, 1);

    this.scriptProcessor.onaudioprocess = (e) => {
      if (!this.isConnected) return;
      const inputData = e.inputBuffer.getChannelData(0);
      let sum = 0;
      for (let i = 0; i < inputData.length; i++) {
        sum += inputData[i] * inputData[i];
      }
      const rms = Math.sqrt(sum / inputData.length);
      this.callbacks.onVolumeChange(rms * 5, 0); 

      const currentSampleRate = this.inputAudioContext?.sampleRate || 16000;
      const pcmBlob = createBlob(inputData, currentSampleRate);
      
      sessionPromise.then((session) => {
        session.sendRealtimeInput({ media: pcmBlob });
      });
    };

    source.connect(this.scriptProcessor);
    this.scriptProcessor.connect(this.inputAudioContext.destination);
  }

  private async handleMessage(message: LiveServerMessage, sessionPromise: Promise<any>) {
    // Handle Input Transcription
    if (message.serverContent?.inputTranscription) {
        this.currentTranscription += message.serverContent.inputTranscription.text;
        this.callbacks.onTranscriptionUpdate(this.currentTranscription);
    }

    if (message.serverContent?.turnComplete) {
        this.currentTranscription = '';
        // Clear recent cache on turn completion to allow re-logging if user explicitly repeats
        // but keep a short window of memory if needed. 
        // For now, clearing turn items prevents "phantom echoes" from previous processing turn.
        this.lastLoggedItemsThisTurn.clear();
    }

    if (message.toolCall) {
      for (const fc of message.toolCall.functionCalls) {
        // Create a signature to detect immediate echoes within a single turn
        const sig = JSON.stringify({ name: fc.name, args: fc.args });
        if (this.lastLoggedItemsThisTurn.has(sig)) {
            console.debug("[GeminiLive] Ignoring duplicate tool call in turn:", sig);
            this.sendSuccessResponse(fc.id, fc.name, sessionPromise);
            continue;
        }
        this.lastLoggedItemsThisTurn.add(sig);

        if (fc.name === 'logFoodItem') {
          const args = fc.args as unknown as LogFoodArgs;
          
          // Additional client-side coarse filtering just in case
          // The System Instruction should catch 99% of this now
          const noiseWords = ['fart', 'merde', 'putain', 'hello', 'salut', 'car', 'shock', 'voiture', 'choc'];
          if (args.name && !noiseWords.includes(args.name.toLowerCase().trim())) {
              this.callbacks.onFoodLogged(args);
          }
          this.sendSuccessResponse(fc.id, fc.name, sessionPromise);
        } 
        else if (fc.name === 'logSymptom') {
          const args = fc.args as unknown as LogSymptomArgs;
          this.callbacks.onSymptomLogged(args);
          this.sendSuccessResponse(fc.id, fc.name, sessionPromise);
        }
        else if (fc.name === 'processVoiceCommand') {
          const args = fc.args as unknown as VoiceCommandArgs;
          this.callbacks.onCommandReceived(args);
          this.sendSuccessResponse(fc.id, fc.name, sessionPromise);
        }
      }
    }
  }

  private sendSuccessResponse(id: string, name: string, sessionPromise: Promise<any>) {
    sessionPromise.then((session) => {
      session.sendToolResponse({
        functionResponses: {
          id: id,
          name: name,
          response: { result: "logged_successfully" },
        },
      });
    });
  }
}
